import { useAuthContext } from "../../auth/state/AuthContext";
import Header from "../../shared/app-shell/Header";
import PageFooter from "../../shared/app-shell/PageFooter";
import Sidebar from "../../shared/app-shell/Sidebar";
import { selectedVesselAtom, themeAtom, userAtom } from "../../shared/atoms";
import LoadingWrapper from "../../shared/components/LoadingWrapper";
import { PaginationList } from "../../shared/components/PaginationList";
import { toastError } from "../../shared/components/ToastContainer";
import { logE, log, logV } from "../../shared/services/debug-logger";
import { api, apiUrl } from "../../shared/services/service";
import { PageNumbers } from "../components/PageNumbers";
import { SearchInput } from "../components/SearchInput";
import { SearchVesselsTable } from "../components/SearchVesselsTable";
import { AdvancedVesselSearchFilters, AdvancedVesselSortField } from "../models/advanced-vessel-search.models";
import { TimeCharter, VesselDetails } from "../models/search.interfaces";
import { searchAdvancedVessels } from "../services/advanced-vessel-search.service";
import { parseSortAndFilterTcs } from "../services/search-vessels.service";
import { CachedItem, tcCodeCacheAtom } from "../state/cache";
import { datadogRum } from "@datadog/browser-rum";
import axios from "axios";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { sortBy } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { twMerge } from "tailwind-merge";

// #region Fetch logic
// TODO: Improve code quality by extracting fetching logic to a separate file and hook with the state to another
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds

const useTcCodeData = (
  setIsLoading: (isLoading: boolean) => void,
  cacheMap: Record<string, CachedItem>,
  setCacheMap: React.Dispatch<React.SetStateAction<Record<string, CachedItem>>>,
) => {
  const [expandData, setExpandData] = useState<TimeCharter[]>([]);

  const fetchTcCodeData = useCallback(
    async (vesselName: string, tcCodeStr: string) => {
      setExpandData([]);
      if (tcCodeStr.trim() === "") {
        return;
      }
      try {
        setIsLoading(true);
        const cacheKey = `tcCodeData_${vesselName}_${tcCodeStr}`;
        const cachedItem = cacheMap[cacheKey];
        if (cachedItem && Date.now() - cachedItem.timestamp < CACHE_TTL) {
          logV("STATE - fetchTcCodeData - cache hit", {
            count: Array.isArray(cachedItem.data) ? cachedItem.data.length : 0,
          });
          setExpandData(cachedItem.data as TimeCharter[]);
          return;
        }
        if (cachedItem) {
          setCacheMap((prev) => {
            const next = { ...prev };
            delete next[cacheKey];
            return next;
          });
        }
        const { tcCodes } = parseSortAndFilterTcs(tcCodeStr);
        const result = await api.get(`${apiUrl}/tccodevoyagelist`, {
          params: {
            vessel_name: vesselName,
            tc_code: tcCodes,
          },
        });
        const order = ["Scheduled", "Commenced", "Completed", "Closed"];
        const sortedVoyages = sortBy(result.data, [
          (voyage: TimeCharter) => order.indexOf(voyage.TC_STATUS),
          (voyage: TimeCharter) => -new Date(voyage.CP_DATE?.trim() || 0).getTime(),
        ]);
        setCacheMap((prev) => ({
          ...prev,
          [cacheKey]: {
            data: sortedVoyages,
            timestamp: Date.now(),
          },
        }));
        log("STATE - fetchTcCodeData - apply", { count: sortedVoyages.length });
        setExpandData(sortedVoyages);
      } catch (error) {
        toastError("Error while fetching data, contact an administrator");
        logE(new Error("STATE - fetchTcCodeData - failed"));
      } finally {
        setIsLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [cacheMap, setCacheMap, setIsLoading],
  );

  return { expandData, fetchTcCodeData };
};
// #endregion

// SECTION 1: Local pagination/count types
type PaginationInfo = { totalRecords: number; totalPages: number };
type RefetchOptions = { forceIncludeCount?: boolean; pageSize?: number };

const SearchVessels: React.FC = () => {
  const user = useAtomValue(userAtom);
  const theme = useAtomValue(themeAtom);
  const setSelectedVessel = useSetAtom(selectedVesselAtom);
  const [cacheMap, setCacheMap] = useAtom(tcCodeCacheAtom);
  const [data, setData] = useState<VesselDetails[] | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [displayCount, setDisplayCount] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedVesselSearchFilters>({});
  const hasAdvancedSearch = Object.values(advancedFilters).some((value) => value !== undefined && value !== "");
  const [isChecked, setIsChecked] = useState(false);
  const [showInactiveVessels, setshowInactiveVessels] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo>({ totalRecords: 0, totalPages: 0 });
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { expandData, fetchTcCodeData: tcCodeGetAPI } = useTcCodeData(setIsLoading, cacheMap, setCacheMap);
  const [showSelectedOnly, setShowSelectedOnly] = useState<boolean>(false);
  const { sub: email, firstname = "", lastname = "" } = useAuthContext().state.user?.accessToken || {};
  // Ensure we always have a usable user_name for vessel endpoints.
  const requestUserName = (user?.name ?? `${firstname} ${lastname}`).trim();

  const typingTimeout = useRef<NodeJS.Timeout>(setTimeout(() => {}));
  const abortControllerRef = useRef<AbortController | null>(null);
  // Keep count metadata by search context to avoid repeated count calls on page navigation.
  const countCacheRef = useRef<Record<string, PaginationInfo>>({});

  const buildCountKey = useCallback(
    (q: string, pageSize: number) =>
      `${q.trim().toLowerCase()}|${showInactiveVessels}|${requestUserName.toLowerCase()}|${pageSize}`,
    [showInactiveVessels, requestUserName],
  );

  useEffect(() => {
    // Wait until user identity is available so initial /getTQ includes user_name.
    if (!requestUserName) {
      return;
    }

    if (searchQuery.trim() === "") {
      // Initial load and cleared search
      refetch(currentPage, "", { forceIncludeCount: true });
      return;
    }

    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      setCurrentPage(1);
      refetch(1, searchQuery, { forceIncludeCount: true });
    }, 500);

    return () => {
      clearTimeout(typingTimeout.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, showInactiveVessels, requestUserName, advancedFilters]);

  useEffect(
    () => () => {
      clearTimeout(typingTimeout.current);
      abortControllerRef.current?.abort();
    },
    [],
  );

  const navigate = useNavigate();

  const getVesselDetails = (vessel: VesselDetails) => {
    // This is a hotfix for not navigating when the vessel name is empty. Refactor the component.
    if (!vessel.VESSEL_NAME) {
      return;
    }
    log("SearchVessels - getVesselDetails", { vesselCode: vessel.VESSEL_CODE });
    setSelectedVessel(vessel);
    const { IMO_NUMBER: imo_no, VESSEL_CODE: vessel_code, VESSEL_NAME: vessel_name } = vessel;
    navigate(`/vessel/details/${imo_no}/${vessel_code}/${vessel_name}`);
  };

  const handleClearClick = async (): Promise<void> => {
    clearTimeout(typingTimeout.current);
    setOpenIndex(null);
    setSearchQuery("");
    setCurrentPage(1);
  };

  // SECTION 2: Refetch strategy with includeCount switch + count cache
  const refetch = async (page: number, searchQuery: string, options: RefetchOptions = {}) => {
    // Close any open accordion before fetching new data
    setOpenIndex(null);
    const normalizedQuery = searchQuery.trim();
    const requestedPageSize = options.pageSize ?? displayCount;
    const requestController = new AbortController();

    datadogRum.addAction("searchVessel", {
      ext_user_email: email,
      search: normalizedQuery,
    });

    try {
      setIsLoading(true);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = requestController;

      // Advanced searches have their own service and totals; default requests and caches stay intact.
      if (hasAdvancedSearch) {
        const response = await searchAdvancedVessels({
          filters: advancedFilters,
          search: normalizedQuery,
          page,
          pageSize: requestedPageSize,
          showInactiveVessels,
          signal: requestController.signal,
        });

        if (requestController.signal.aborted) {
          return;
        }

        setData(response.data?.data ?? []);
        setCurrentPage(Number(response.data?.currentPage || page));
        setPaginationInfo({
          totalRecords: Number(response.data?.totalRecords ?? 0),
          totalPages: Number(response.data?.totalPages ?? 0),
        });
        document.getElementById("main")?.scrollTo({ top: 0 });

        return;
      }

      const endpoint = normalizedQuery === "" ? "getTQ" : "searchVessel";
      const countKey = buildCountKey(normalizedQuery, requestedPageSize);
      const cachedCounts = countCacheRef.current[countKey];
      const supportsOptionalCount = endpoint === "searchVessel" || endpoint === "getTQ";

      // Request count only when needed (first page/new filter/search), skip it for page navigation.
      const includeCount = supportsOptionalCount ? options.forceIncludeCount ?? (page === 1 || !cachedCounts) : true;

      const response = await api.get(`${apiUrl}/${endpoint}`, {
        signal: abortControllerRef.current.signal,
        params: {
          user_name: requestUserName,
          showInactiveVessels: showInactiveVessels.toString(),
          ...(normalizedQuery !== "" ? { vessel_name: normalizedQuery } : {}),
          page,
          pageSize: requestedPageSize,
          ...(supportsOptionalCount ? { includeCount: includeCount.toString() } : {}),
        },
      });

      const rows: VesselDetails[] = response.data?.data ?? [];

      if (requestController.signal.aborted) {
        return;
      }
      log("STATE - refetch - vessels", { count: rows.length, page });
      setData(rows);

      // Sync with backend (if it clamps to a valid page)
      const serverPage = Number(response.data?.currentPage || page);
      if (serverPage !== currentPage) {
        setCurrentPage(serverPage);
      }

      // Update totals only when backend provides them; otherwise reuse cached totals.
      const rawTotalRecords = response.data?.totalRecords;
      const rawTotalPages = response.data?.totalPages;
      const hasServerTotals =
        rawTotalRecords !== null &&
        rawTotalRecords !== undefined &&
        rawTotalPages !== null &&
        rawTotalPages !== undefined;

      if (hasServerTotals) {
        const nextInfo: PaginationInfo = {
          totalRecords: Number(rawTotalRecords),
          totalPages: Number(rawTotalPages),
        };
        countCacheRef.current[countKey] = nextInfo;
        setPaginationInfo(nextInfo);
      } else if (cachedCounts) {
        setPaginationInfo(cachedCounts);
      }

      // If page is stale and count was skipped, retry once with includeCount=true.
      if (supportsOptionalCount && !includeCount && page > 1 && rows.length === 0) {
        log("SearchVessels - refetch - retry count", { page });
        await refetch(page, normalizedQuery, { forceIncludeCount: true, pageSize: requestedPageSize });
        return;
      }

      document.getElementById("main")?.scrollTo({ top: 0 });
    } catch (error: unknown) {
      if (!requestController.signal.aborted && !axios.isCancel(error)) {
        if (axios.isAxiosError(error)) {
          setData([]);
        }
        if (hasAdvancedSearch) {
          setPaginationInfo({ totalRecords: 0, totalPages: 0 });
          toastError("Unable to search vessels. Please try again or adjust your filters.");
        }
        logE(
          new Error(
            `SearchVessels - refetch - failed - status ${axios.isAxiosError(error) ? error.response?.status ?? "unavailable" : "unavailable"}`,
          ),
        );
      }
    } finally {
      if (abortControllerRef.current === requestController && !requestController.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const handleSearchClick = async (): Promise<void> => {
    log("SearchVessels - handleSearchClick");
    clearTimeout(typingTimeout.current);
    const q = searchQuery.trim();
    if (currentPage !== 1) setCurrentPage(1);
    await refetch(1, q === "" ? "" : q, { forceIncludeCount: true });
  };

  const applyAdvancedFilters = (filters: AdvancedVesselSearchFilters) => {
    clearTimeout(typingTimeout.current);
    abortControllerRef.current?.abort();
    setOpenIndex(null);
    setCurrentPage(1);
    setAdvancedFilters({ ...filters });
  };

  const sortVessels = (field: AdvancedVesselSortField) => {
    applyAdvancedFilters({
      ...advancedFilters,
      sortBy: field,
      sortDirection: advancedFilters.sortBy === field && advancedFilters.sortDirection === "asc" ? "desc" : "asc",
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputValue = e.target.value;
    setSearchQuery(inputValue);
  };

  const handlePageChange = async (newPage: number) => {
    log("SearchVessels - handlePageChange", { page: newPage });
    setCurrentPage(newPage);
    setOpenIndex(null);
    await refetch(newPage, searchQuery, { forceIncludeCount: false, pageSize: displayCount });
  };

  const toggleShowSelectedOnly = () => {
    setShowSelectedOnly(!showSelectedOnly);
    setIsChecked(!isChecked);
  };

  const toggleShowInactiveVessels = async () => {
    setOpenIndex(null);
    setCurrentPage(1); // optional but recommended
    setshowInactiveVessels((prev) => !prev);
  };

  const toggleAccordion = (toggleIndex: number, item: VesselDetails): void => {
    setSelectedVessel(item);
    setOpenIndex(toggleIndex === openIndex ? null : toggleIndex);
    if (toggleIndex !== openIndex) {
      tcCodeGetAPI(item.VESSEL_NAME, item.TCCODE_TCSTATUS_CPDELDT_CPREDELDT_VOYAGENO).catch((err) =>
        logE(new Error("SearchVessels - toggleAccordion - failed")),
      );
    }
  };

  const handleDisplayCountChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDisplayCount = Number(event.target.value);
    setDisplayCount(nextDisplayCount);
    setCurrentPage(1);
    setOpenIndex(null);
    void refetch(1, searchQuery, { forceIncludeCount: true, pageSize: nextDisplayCount });
  };

  logV("SearchVessels - BUILD", { loading: isLoading, count: data?.length ?? 0 });

  return (
    <LoadingWrapper isLoading={isLoading} loadingIndicatorClassName="text-green" loadingIndicatorSize="lg">
      <div className={twMerge("flex min-h-screen items-stretch")}>
        <Sidebar />
        <div className={twMerge("flex flex-1 flex-col", theme.home.tailwind.pageBackground)}>
          <Header />

          <h1
            className={twMerge(
              "text-custom-color pb-3 pl-6 pt-8 text-2xl font-semibold md:mb-0 md:mr-4",
              theme.myVesselsTable.tailwind.textDefault,
            )}
          >
            Vessels
          </h1>
          <h3
            className={twMerge(
              "pb-6 pl-6 text-sm font-normal md:mb-0 md:mr-4",
              theme.myVesselsTable.tailwind.textDefault,
            )}
          >
            Keep track of all the vessels in Cargill. Expand each row to explore the timecharters and corresponding
            voyages.
          </h3>

          <main id="main">
            <ToastContainer />
            <div>
              <div className={twMerge("rounded-lg p-0")}>
                <SearchInput
                  searchQuery={searchQuery}
                  handleInputChange={handleInputChange}
                  handleSearchClick={handleSearchClick}
                  handleClearClick={handleClearClick}
                  isChecked={isChecked}
                  toggleShowSelectedOnly={toggleShowSelectedOnly}
                  showInactiveVessels={showInactiveVessels}
                  toggleShowInactiveVessels={toggleShowInactiveVessels}
                  advancedFilters={advancedFilters}
                  onApplyAdvancedFilters={applyAdvancedFilters}
                  hasAdvancedSearch={hasAdvancedSearch}
                />
                <div className="overflow-y-auto">
                  <SearchVesselsTable
                    data={data}
                    searchQuery={searchQuery}
                    openIndex={openIndex}
                    toggleAccordion={toggleAccordion}
                    showSelectedOnly={showSelectedOnly}
                    fullName={user?.name ?? ""}
                    handleVesselNameClick={getVesselDetails}
                    timeCharters={expandData}
                    setIsLoading={setIsLoading}
                    sortBy={advancedFilters.sortBy}
                    sortDirection={advancedFilters.sortDirection}
                    onSort={sortVessels}
                    hasAdvancedSearch={hasAdvancedSearch}
                  />
                  <PaginationList
                    handlePageChange={handlePageChange}
                    currentPage={currentPage}
                    PageNumbersComponent={PageNumbers}
                    displayCount={displayCount}
                    handleDisplayCountChange={handleDisplayCountChange}
                    totalItems={paginationInfo.totalRecords}
                    totalPages={paginationInfo.totalPages}
                  />
                </div>
              </div>
            </div>
          </main>

          <PageFooter className="bg-gray-100" />
        </div>
      </div>
    </LoadingWrapper>
  );
};

export default SearchVessels;
